import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { cn } from '@/lib/utils';
import type { BrowseMode } from '@/hooks/useUnitFilters';

export interface FilterOption {
  label: string;
  value: string;
  count?: number;
}

interface ResidencesFilterBarProps {
  filters: FilterOption[];
  activeFilter: string;
  onFilterChange: (value: string) => void;
  browseMode?: BrowseMode;
  loading?: boolean;
  className?: string;
}

export const ResidencesFilterBar = ({
  filters,
  activeFilter,
  onFilterChange,
  browseMode = 'all-units',
  loading = false,
  className,
}: ResidencesFilterBarProps) => {
  // Filter options based on browse mode
  const relevantFilters = browseMode === 'featured'
    ? filters.filter(f => ['all', '1bed', '2bed', 'townhouse'].includes(f.value))
    : filters;

  // Loading skeleton state
  if (loading) {
    return (
      <div 
        className={cn('flex flex-wrap justify-center gap-2 lg:gap-4', className)}
        role="status"
        aria-live="polite"
        aria-label="Loading filter options"
      >
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-11 w-32 animate-pulse" />
        ))}
        <span className="sr-only">Loading filters...</span>
      </div>
    );
  }

  return (
    <nav
      className={cn('relative w-full', className)}
      role="navigation"
      aria-label="Unit filter navigation"
    >
      {/* Mobile & Desktop: Wrapped layout */}
      <div className="flex flex-wrap justify-center gap-2 lg:gap-3" role="group" aria-label="Filter buttons">
        <TooltipProvider delayDuration={300}>
          {relevantFilters.map((filter) => {
            const isActive = activeFilter === filter.value;
            const hasNoResults = filter.count === 0 && filter.value !== 'all';
            const isDisabled = hasNoResults;

            const button = (
              <Button
                key={filter.value}
                variant={isActive ? 'default' : 'outline'}
                onClick={() => !isDisabled && onFilterChange(filter.value)}
                disabled={isDisabled}
                className={cn(
                  'px-5 py-2.5 transition-all duration-300 min-w-[100px] lg:min-w-[100px] relative group',
                  isActive && 'bg-brand-teal text-white hover:bg-brand-navy shadow-lg scale-105 border-brand-teal',
                  !isActive && !isDisabled && 'hover:bg-brand-teal/10 hover:border-brand-teal hover:text-foreground hover:scale-102',
                  isDisabled && 'opacity-40 cursor-not-allowed'
                )}
                aria-pressed={isActive}
                aria-disabled={isDisabled}
                aria-label={`${filter.label}${filter.count !== undefined ? `, ${filter.count} units` : ''}`}
                tabIndex={isDisabled ? -1 : 0}
              >
                <span className="font-medium">{filter.label}</span>
                {filter.count !== undefined && (
                  <Badge 
                    variant={isActive ? 'secondary' : 'outline'} 
                    className={cn(
                      'ml-2 text-xs font-bold min-w-[24px] justify-center transition-colors',
                      isActive && 'bg-primary text-brand-teal border-primary',
                      !isActive && 'bg-brand-teal/10 text-foreground border-brand-teal/30'
                    )}
                    aria-hidden="true"
                  >
                    {filter.count}
                  </Badge>
                )}
              </Button>
            );

            if (isDisabled) {
              return (
                <Tooltip key={filter.value}>
                  <TooltipTrigger asChild>
                    {button}
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-sm">No units available with this filter</p>
                  </TooltipContent>
                </Tooltip>
              );
            }

            return button;
          })}
        </TooltipProvider>
      </div>
    </nav>
  );
};

